import { Controller } from "@hotwired/stimulus"

// Generic color picker <-> hex text sync controller
// The color picker is embedded in the "#" prefix, and the text input background shows the color
// Usage:
// <div data-controller="color-picker">
//   <div class="input-group">
//     <input type="color" data-color-picker-target="picker" class="color-picker-prefix">
//     <input type="text" data-color-picker-target="text">
//   </div>
// </div>

export default class extends Controller {
  static targets = ["picker", "text"]

  connect() {
    this.syncFromPicker()
    this.updateTextBackground()
  }

  pickerChanged() {
    this.syncFromPicker()
    this.updateTextBackground()
  }

  textChanged() {
    this.syncFromText()
    this.updateTextBackground()
  }

  syncFromPicker() {
    if (!this.hasPickerTarget || !this.hasTextTarget) return
    let color = this.pickerTarget.value || ""
    // Strip leading # for text field
    if (color.startsWith("#")) {
      color = color.substring(1)
    }
    this.textTarget.value = color
  }

  syncFromText() {
    if (!this.hasPickerTarget || !this.hasTextTarget) return
    let value = this.textTarget.value.trim()
    // Accept 3 or 6 hex chars; ignore others
    if (/^[0-9a-fA-F]{3}$/.test(value) || /^[0-9a-fA-F]{6}$/.test(value)) {
      // Expand 3-digit hex to 6 for the color input
      if (value.length === 3) {
        value = value.split("").map(ch => ch + ch).join("")
      }
      this.pickerTarget.value = `#${value}`
    }
  }

  updateTextBackground() {
    if (!this.hasPickerTarget || !this.hasTextTarget) return
    const color = this.pickerTarget.value || "#ffffff"
    this.textTarget.style.backgroundColor = color

    const rgb = this.hexToRgb(color)
    if (rgb) {
      const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
      const textColor = brightness > 128 ? "#000000" : "#ffffff"
      this.textTarget.style.color = textColor
      this.pickerTarget.style.color = textColor
    } else {
      this.textTarget.style.color = "#000000"
      this.pickerTarget.style.color = "#000000"
    }
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }
}

